import { useDashboardContext } from '../DashboardContext';
import { useMemo } from 'react';
import style from './Content.module.scss';
import { MousePosition } from '@react-hook/mouse-position/src';
import { ModuleData } from '../Types';
import { useAutoRotateHoveredModule } from './useAutoRotateHoveredModule';

export const getModuleX = (total: number, i: number, width = MODULE_WIDTH, maxWidth = MAX_WIDTH) =>
    48 - ((i - total / 2) * (maxWidth - width)) / total - width / 2;

export const MODULE_WIDTH = 20;
export const MAX_WIDTH = 70;

export const edgeStartX = -100 + (100 - MAX_WIDTH);
export const edgeEndX = MAX_WIDTH + (100 - MAX_WIDTH) / 2 - MODULE_WIDTH / 2;
export const maskFillX = 100 - MAX_WIDTH;

const isActiveFactory = (selected: string | null, hovered: string | null) => (current: string) =>
    selected === current || (selected === null && hovered === current);

const getDiamondId = (m: ModuleData, i: number, postFix: string = '') => `diamond-${i}-${postFix}`;

export const ContentDefs = () => {
    const { modules } = useDashboardContext();

    const modulesArray = useMemo(() => Object.values(modules).reverse(), [modules]);

    return (
        <>
            <filter id="invert">
                <feColorMatrix
                    in="SourceGraphic"
                    type="matrix"
                    values="-1 0 0 0 1
                                                              0 -1 0 0 1
                                                              0 0 -1 0 1
                                                              0 0 0 1 0"
                />
            </filter>

            <linearGradient id="colorizeGradient" x1="1" x2="0" y1="0" y2="0">
                <stop offset="00%" stopColor="#FA7E3E" />
                <stop offset="100%" stopColor="#FFBE01" />
            </linearGradient>

            <linearGradient id="diamondStroke" x1="1" x2="0" y1="0" y2="0">
                <stop offset="00%" stopColor="white" />
                <stop offset="70%" stopColor="black" />
            </linearGradient>

            <linearGradient id="selectionGradient" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="white" />
                <stop offset="30%" stopColor="white" />
                <stop offset="50%" stopColor="transparent" />
                <stop offset="70%" stopColor="white" />
                <stop offset="100%" stopColor="white" />
            </linearGradient>

            <linearGradient id="diamondStrokeStart" x1="1" x2="0" y1="0" y2="0">
                <stop offset="00%" stopColor="white" />
                <stop offset="50%" stopColor="black" />
            </linearGradient>

            <linearGradient id="diamondStrokeEnd" x1="1" x2="0" y1="0" y2="0">
                <stop offset="00%" stopColor="black" />
                <stop offset="30%" stopColor="white" />
                <stop offset="90%" stopColor="black" />
            </linearGradient>

            {modulesArray.map((m, i) => (
                <symbol key={i} id={getDiamondId(m, i)} width="140" height="320" viewBox="0 0 140 320" fill="none">
                    <path id={getDiamondId(m, i, 'path')} fill="none" d="M70,0 L140,160"></path>
                    <polygon
                        vectorEffect="non-scaling-stroke"
                        points="70,0, 140,160, 70,320, 0,160"
                        className="2xl:stroke-2"
                        stroke="url(#diamondStroke)"
                        fill="black"
                        fillOpacity="70%"
                        strokeDasharray={m.isDisabled ? '2%' : undefined}></polygon>
                    <text fill="white" textAnchor="middle" fontSize="12" transform="translate(-12 15)">
                        <textPath startOffset="50%" href={'#' + getDiamondId(m, i, 'path')}>
                            {m.label}
                        </textPath>
                    </text>
                </symbol>
            ))}

            <symbol
                id="diamondStart"
                width="140"
                height="320"
                viewBox="0 0 140 320"
                fill="none"
                preserveAspectRatio="xMaxYMin">
                <polygon
                    vectorEffect="non-scaling-stroke"
                    points="0,0, 70,0, 140,160, 70,320, 0,320"
                    className="2xl:stroke-2"
                    stroke="url(#diamondStrokeStart)"
                    fill="black"
                    fillOpacity="70%"></polygon>
            </symbol>

            <symbol
                id="diamondEnd"
                width="140"
                height="320"
                viewBox="0 0 140 320"
                fill="none"
                preserveAspectRatio="xMinYMin">
                <polygon
                    vectorEffect="non-scaling-stroke"
                    points="70,0, 140,0, 140,320, 70,320, 0,160"
                    className="2xl:stroke-2"
                    stroke="url(#diamondStrokeEnd)"
                    fill="black"
                    fillOpacity="70%"></polygon>
            </symbol>

            <symbol id="diamondBg" width="140" height="320" viewBox="0 0 140 320" fill="none">
                <polygon vectorEffect="non-scaling-stroke" points="70,0, 140,160, 70,320, 0,160" fill="black"></polygon>
            </symbol>

            <linearGradient id="moduleInfo" x1="0" x2="1" y1="0" y2="0">
                <stop offset="0%" stopColor="black" />
                <stop offset="30%" stopColor="rgb(170, 170, 170)" />
                <stop offset="70%" stopColor="rgb(170, 170, 170)" />
                <stop offset="100%" stopColor="black" />
            </linearGradient>
        </>
    );
};

export const Content = ({ mouse }: { mouse: MousePosition }) => {
    const {
        modules,
        setHoveredModule,
        setSelectedModule,
        selectedModule,
        hoveredModule,
        isDashboardAnimationsEnabled
    } = useDashboardContext();

    const { pause } = useAutoRotateHoveredModule(5 * 60 * 1000, 3600); // todo: move times to redis

    const modulesArray = useMemo(
        () =>
            Object.entries(modules)
                .reverse()
                .map(([key, m]) => ({ ...m, key })),
        [modules]
    );

    const handleManualSelect = (key: string | null) => {
        setSelectedModule(key);
        pause();
    };

    const handleManualHover = (key: string | null) => {
        setHoveredModule(key);
        pause();
    };

    const last = modulesArray.at(-1);

    const isActive = isActiveFactory(selectedModule, hoveredModule);

    return (
        <>
            <mask id="content">
                <use href="#diamondEnd" x={edgeEndX + '%'} y="-3%" width="100%" height="106%" />
                <g>
                    {modulesArray.map((m, i, arr) => (
                        <g key={i} className={[style.diamond, isActive(m.key) ? style.active : ''].join(' ')}>
                            <use
                                href={'#' + getDiamondId(m, i)}
                                x={getModuleX(arr.length, i) + '%'}
                                y="20%"
                                width="20%"
                                height="60%"
                            />
                        </g>
                    ))}
                </g>
                <use
                    href="#diamondStart"
                    className="origin-top-right"
                    x={edgeStartX + '%'}
                    y="-3%"
                    width="100%"
                    height="106%"
                />
            </mask>

            <mask id="contentMaskForBg">
                <g filter="url(#invert)">
                    <rect width="100%" height="100%" fill="white"></rect>
                    <use
                        href="#startMask"
                        className="origin-top-right"
                        x={edgeStartX + '%'}
                        y="0%"
                        width="100%"
                        height="100%"
                    />
                    <use
                        href="#endMask"
                        className="origin-top-right"
                        x={edgeEndX + '%'}
                        y="0%"
                        width="100%"
                        height="100%"
                    />
                    <rect width={edgeEndX - maskFillX + '%'} height="100%" x={maskFillX + '%'} fill="black" />
                    <g className={[style.diamond, isActive(last?.key ?? '') ? style.active : ''].join(' ')}>
                        <use
                            href="#diamondBg"
                            x={getModuleX(modulesArray.length, 0) + '%'}
                            y="20%"
                            width="20%"
                            height="60%"
                        />
                    </g>
                </g>
            </mask>

            <mask id="selectedContent">
                <g>
                    {modulesArray.map((m, i, arr) => (
                        <g
                            key={i}
                            className={[
                                style.diamond,
                                style.selectionMask,
                                isActive(m.key) ? style.active : '',
                                selectedModule === m.key ? style.selected : ''
                            ].join(' ')}>
                            <use
                                href={'#' + getDiamondId(m, i)}
                                x={getModuleX(arr.length, i, MODULE_WIDTH, MAX_WIDTH) + '%'}
                                y="20%"
                                width="20%"
                                height="60%"
                            />
                        </g>
                    ))}
                </g>
            </mask>

            <g opacity="70%" mask="url(#contentMaskForBg)">
                {modulesArray.map((m, i, arr) => (
                    <g key={i} className={[style.diamond, isActive(m.key) ? style.active : ''].join(' ')}>
                        <use
                            onMouseEnter={() => handleManualHover(m.key)}
                            onMouseOut={() => (hoveredModule === m.key ? handleManualHover(null) : null)}
                            onClick={() =>
                                selectedModule === m.key ? handleManualSelect(null) : handleManualSelect(m.key)
                            }
                            href="#diamondBg"
                            className="cursor-pointer"
                            x={getModuleX(arr.length, i, MODULE_WIDTH, MAX_WIDTH) + '%'}
                            y="20%"
                            width="20%"
                            height="60%"
                        />
                    </g>
                ))}
            </g>
            <rect
                width="100%"
                height="100%"
                fill="url(#colorizeGradient)"
                mask="url(#content)"
                className="pointer-events-none"></rect>

            {isDashboardAnimationsEnabled && mouse.x != null && mouse.y != null ? (
                <circle
                    cx={mouse.x}
                    cy={mouse.y}
                    r="100"
                    mask="url(#content)"
                    fill="url('#mouse')"
                    className="pointer-events-none"></circle>
            ) : (
                <></>
            )}

            <rect
                width="100%"
                height="100%"
                fill="url(#selectionGradient)"
                mask="url(#selectedContent)"
                className="pointer-events-none"></rect>
        </>
    );
};
